import { useState, useEffect, useRef } from "react"
import { useStore } from "../store"
import { Send, MessageCircle, Mic, MicOff, Download, FileText, MessageSquare, Image, Search } from "lucide-react"
import api from "../services/api"

type ChatMode = 'text' | 'image' | 'search'

export default function ChatPage() {
  const [inputValue, setInputValue] = useState("")
  const [chatMode, setChatMode] = useState<ChatMode>('text')
  const [isRecording, setIsRecording] = useState(false)
  const [providers, setProviders] = useState<any[]>([])
  const [elapsedSecs, setElapsedSecs] = useState(0)
  const loadingStartRef = useRef<number | null>(null)
  useEffect(() => {
    if (isLoading || isStreaming) {
      if (!loadingStartRef.current) {
        loadingStartRef.current = Date.now()
        setElapsedSecs(0)
      }
      const id = window.setInterval(() => {
        if (loadingStartRef.current) {
          setElapsedSecs(Math.floor((Date.now() - loadingStartRef.current) / 1000))
        }
      }, 1000)
      return () => window.clearInterval(id)
    } else {
      loadingStartRef.current = null
      setElapsedSecs(0)
    }
  }, [isLoading, isStreaming])
  const [showExportMenu, setShowExportMenu] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<any>(null)

  const {
    currentConversation, messages, isLoading, isStreaming, streamingContent, error,
    sendMessage, fetchSkills, setError, selectedProviderId, setSelectedProviderId, selectedSkillId
  } = useStore()

  useEffect(() => { fetchSkills() }, [fetchSkills])
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }) }, [messages, streamingContent])

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const response = await api.get('/ai-providers/')
        setProviders(response.data || [])
        if (response.data?.length > 0 && !selectedProviderId) {
          setSelectedProviderId(String(response.data[0].id))
        }
      } catch {}
    }
    loadProviders()
  }, [])

  // Estimation tokens (4 chars ≈ 1 token)
  const estimateTokens = (text: string) => Math.ceil(text.length / 4)
  const totalTokens = messages.reduce((acc, m) => acc + estimateTokens(m.content), 0)

  // Prix par 1M tokens selon modèle (affiché dans dropdown)
  const MODEL_PRICE_PER_1M: Record<string, string> = {
    // Mistral
    'mistral-large-latest': '$2/1M',
    'mistral-small-latest': '$0.10/1M',
    'ministral-8b-latest': '$0.10/1M',
    'labs-mistral-small-creative': '$0.10/1M',
    // Anthropic
    'claude-opus-4-6': '$15/1M',
    'claude-sonnet-4-6': '$3/1M',
    'claude-haiku-4-5-20251001': '$1/1M',
    // OpenAI
    'gpt-4o': '$5/1M',
    'gpt-4o-mini': '$0.15/1M',
    'gpt-4-turbo': '$10/1M',
    'o3': '$10/1M',
    'o4-mini': '$1.10/1M',
    // Google
    'gemini-2.0-flash': '$0.10/1M',
    'gemini-2.0-flash-lite': '$0.075/1M',
    'gemini-1.5-flash': '$0.30/1M',
    'gemini-1.5-pro': '$3.50/1M',
    'gemini-2.5-pro-exp-03-25': '$1.25/1M',
    // Perplexity
    'sonar-pro': '$3/1M',
    'sonar': '$1/1M',
    'sonar-reasoning': '$5/1M',
    'sonar-reasoning-pro': '$8/1M',
  }

  const getProviderLabel = (p: any) => {
    const model = p.config?.model || ''
    const price = MODEL_PRICE_PER_1M[model]
    if (p.type === 'local') return `${p.name} — gratuit`
    return price ? `${p.name} — ${price}` : p.name
  }

  const getCostPer1K = () => {
    const provider = providers.find(p => p.id === selectedProviderId)
    if (!provider) return 0
    if (provider.type === 'anthropic') return 0.003
    if (provider.type === 'openai') return 0.005
    if (provider.type === 'google') return 0.00035
    if (provider.type === 'perplexity') return 0.001
    if (provider.type === 'mistral') return 0.0001
    return 0
  }
  const estimatedCost = (totalTokens / 1000) * getCostPer1K()

  // Dictée vocale via Web Speech API
  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop()
      setIsRecording(false)
      return
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      alert("Votre navigateur ne supporte pas la reconnaissance vocale (essayez Chrome)")
      return
    }
    const recognition = new SpeechRecognition()
    recognition.lang = 'fr-FR'
    recognition.continuous = false
    recognition.interimResults = false
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript
      setInputValue(prev => (prev ? prev + ' ' : '') + transcript)
      setIsRecording(false)
    }
    recognition.onerror = () => setIsRecording(false)
    recognition.onend = () => setIsRecording(false)
    recognition.start()
    recognitionRef.current = recognition
    setIsRecording(true)
  }

  // Export Markdown
  const exportMarkdown = () => {
    if (!currentConversation) return
    let md = `# ${currentConversation.title}\n\n*Exporté le ${new Date().toLocaleDateString('fr-FR')}*\n\n---\n\n`
    messages.forEach(m => {
      md += `**${m.role === 'user' ? '👤 Utilisateur' : '🤖 Assistant'}**\n\n${m.content}\n\n`
      if (m.model_name) md += `*Modèle: ${m.model_name} · ~${estimateTokens(m.content)} tokens*\n\n`
      md += `---\n\n`
    })
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${currentConversation.title.replace(/\s+/g, '_')}.md`
    a.click()
    URL.revokeObjectURL(url)
    setShowExportMenu(false)
  }

  // Export HTML
  const exportHTML = () => {
    if (!currentConversation) return
    const rows = messages.map(m => {
      const safeContent = m.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const meta = m.model_name ? `<div class="meta">${m.model_name} · ~${estimateTokens(m.content)} tokens</div>` : ''
      return `<div class="msg ${m.role}"><p>${safeContent}</p>${meta}</div>`
    }).join('')
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${currentConversation.title}</title>
<style>body{font-family:system-ui,sans-serif;background:#111827;color:#f3f4f6;max-width:800px;margin:0 auto;padding:24px}
h1{color:#ec4899;margin-bottom:4px}p.sub{color:#9ca3af;font-size:13px;margin-bottom:24px}
.msg{margin:12px 0;padding:14px 18px;border-radius:10px;max-width:75%}
.user{background:#ec4899;margin-left:auto}.assistant{background:#1f2937;border:1px solid #374151}
.meta{font-size:11px;color:#a78bfa;margin-top:8px}.msg p{margin:0;white-space:pre-wrap}</style>
</head><body><h1>${currentConversation.title}</h1>
<p class="sub">Exporté le ${new Date().toLocaleDateString('fr-FR')} — ${messages.length} messages — ~${totalTokens} tokens</p>
${rows}</body></html>`
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${currentConversation.title.replace(/\s+/g, '_')}.html`
    a.click()
    URL.revokeObjectURL(url)
    setShowExportMenu(false)
  }

  const handleSend = async () => {
    if (!inputValue.trim() || !currentConversation) return
    const msg = inputValue
    setInputValue("")
    useStore.setState(state => ({
      messages: [...state.messages, {
        id: "u" + Date.now(),
        conversation_id: currentConversation.id,
        role: "user",
        content: msg,
        created_at: new Date().toISOString(),
        sources: [],
        provider_used: undefined,
        model_name: undefined
      }]
    }))
    await sendMessage(msg, selectedSkillId || undefined, true, selectedProviderId || undefined)
    if (error) setTimeout(() => setError(null), 5000)
  }

  const modeConfig = {
    text:   { icon: <MessageSquare size={13} />, label: 'Texte',    placeholder: 'Votre message...' },
    image:  { icon: <Image size={13} />,         label: 'Image',    placeholder: "Décrivez l'image à générer..." },
    search: { icon: <Search size={13} />,        label: 'Recherche', placeholder: 'Rechercher sur le web...' },
  }

  const modeColor = { text: '#ec4899', image: '#8b5cf6', search: '#06b6d4' }
  const selectedProvider = providers.find(p => p.id === selectedProviderId)

  if (!currentConversation) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: '#111827', background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)' }}>
        <div style={{ textAlign: 'center' }}>
          <MessageCircle size={48} style={{ color: '#ec4899', marginBottom: '16px', opacity: 0.6 }} />
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#f3f4f6', marginBottom: '8px' }}>Aucune conversation</h2>
          <p style={{ color: '#9ca3af' }}>Créez ou sélectionnez une discussion dans la sidebar</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#111827' }}>

      {/* ── Header ── */}
      <div style={{ borderBottom: '1px solid #374151', backgroundColor: '#1f2937', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <h1 style={{ fontSize: '15px', fontWeight: 'bold', color: '#ffffff', margin: '0 0 3px 0' }}>{currentConversation.title}</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ color: '#6b7280', fontSize: '12px' }}>{messages.length} messages</span>
            <span style={{ color: '#a78bfa', fontSize: '12px' }}>~{totalTokens.toLocaleString()} tokens</span>
            {estimatedCost > 0 ? (
              <span style={{ color: '#34d399', fontSize: '12px' }}>${estimatedCost.toFixed(4)}</span>
            ) : selectedProvider?.type !== 'local' && selectedProvider ? null : (
              <span style={{ color: '#34d399', fontSize: '12px' }}>Gratuit (local)</span>
            )}
            {selectedProvider && (
              <span style={{ color: '#ec4899', fontSize: '11px', backgroundColor: 'rgba(236,72,153,0.1)', padding: '2px 8px', borderRadius: '10px', border: '1px solid rgba(236,72,153,0.3)' }}>
                {selectedProvider.name}
              </span>
            )}
          </div>
        </div>

        {/* Export dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            style={{ padding: '6px 14px', backgroundColor: '#374151', color: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#4b5563'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#374151'}
          >
            <Download size={13} /> Exporter
          </button>
          {showExportMenu && (
            <div style={{ position: 'absolute', right: 0, top: '34px', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '4px', zIndex: 100, minWidth: '160px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              <button onClick={exportMarkdown} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', backgroundColor: 'transparent', color: '#f3f4f6', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#374151'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <FileText size={14} style={{ color: '#a78bfa' }} /> Markdown (.md)
              </button>
              <button onClick={exportHTML} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', backgroundColor: 'transparent', color: '#f3f4f6', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#374151'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <FileText size={14} style={{ color: '#06b6d4' }} /> HTML (.html)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {messages.length === 0 && !isStreaming ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '12px' }}>
            <MessageCircle size={32} style={{ color: '#374151' }} />
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Démarrez une conversation...</span>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  backgroundColor: m.role === 'user' ? '#ec4899' : '#1f2937',
                  color: '#ffffff',
                  maxWidth: '72%',
                  padding: '12px 16px',
                  borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  wordBreak: 'break-word',
                  border: m.role === 'assistant' ? '1px solid #374151' : 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  <p style={{ fontSize: '13px', whiteSpace: 'pre-wrap', margin: '0', lineHeight: '1.6' }}>{m.content}</p>
                  {(m.model_name || m.role === 'assistant') && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#a78bfa', display: 'flex', gap: '8px' }}>
                      {m.model_name && <span>{m.model_name}</span>}
                      <span>~{estimateTokens(m.content)} tokens</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isStreaming && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ backgroundColor: '#1f2937', color: '#f3f4f6', maxWidth: '72%', padding: '12px 16px', borderRadius: '12px 12px 12px 2px', wordBreak: 'break-word', border: '1px solid #4b5563' }}>
                  {streamingContent ? (
                    <p style={{ fontSize: '13px', whiteSpace: 'pre-wrap', margin: '0', lineHeight: '1.6' }}>
                      {streamingContent}
                      <span style={{ display: 'inline-block', width: '2px', height: '14px', backgroundColor: '#ec4899', marginLeft: '2px', verticalAlign: 'text-bottom', animation: 'blink 1s step-end infinite' }}>▋</span>
                    </p>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: '0' }}>▪▪▪</p>
                  )}
                  {elapsedSecs > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ec4899', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      {elapsedSecs >= 60 ? `${Math.floor(elapsedSecs / 60)}m ${elapsedSecs % 60}s` : `${elapsedSecs}s`}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* ── Toolbar bas ── */}
      <div style={{ borderTop: '1px solid #374151', backgroundColor: '#1f2937', flexShrink: 0 }}>

        {/* Ligne 1 : sélecteur mode */}
        <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Tabs mode */}
          <div style={{ display: 'flex', gap: '2px', backgroundColor: '#111827', padding: '3px', borderRadius: '8px' }}>
            {(['text', 'image', 'search'] as ChatMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setChatMode(mode)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '5px 11px', borderRadius: '6px', border: 'none',
                  cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                  backgroundColor: chatMode === mode ? modeColor[mode] : 'transparent',
                  color: chatMode === mode ? '#ffffff' : '#6b7280',
                  transition: 'all 0.15s'
                }}
              >
                {modeConfig[mode].icon}
                {modeConfig[mode].label}
              </button>
            ))}
          </div>
        </div>

        {/* Ligne 2 : micro + input + envoyer */}
        <div style={{ padding: '8px 20px 14px', display: 'flex', gap: '8px', alignItems: 'center' }}>

          {/* Bouton micro */}
          <button
            onClick={toggleRecording}
            title={isRecording ? "Arrêter l'enregistrement" : "Dictée vocale (Whisper)"}
            style={{
              padding: '9px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              backgroundColor: isRecording ? '#dc2626' : '#374151',
              color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', flexShrink: 0,
              boxShadow: isRecording ? '0 0 0 3px rgba(220,38,38,0.3)' : 'none',
              animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }}
          >
            {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
          </button>

          <input
            type="text"
            autoComplete="off"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={modeConfig[chatMode].placeholder}
            style={{ flex: 1, padding: '9px 12px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: '#f3f4f6', fontSize: '13px', outline: 'none' }}
            onFocus={(e) => e.currentTarget.style.borderColor = modeColor[chatMode]}
            onBlur={(e) => e.currentTarget.style.borderColor = '#374151'}
            disabled={isLoading}
          />

          {/* Sélecteur modèle inline */}
          <select
            value={selectedProviderId || ''}
            onChange={(e) => setSelectedProviderId(e.target.value)}
            style={{
              backgroundColor: '#111827', border: '1px solid #374151', color: '#f3f4f6',
              padding: '9px 10px', borderRadius: '6px', fontSize: '11px', outline: 'none',
              cursor: 'pointer', maxWidth: '180px', flexShrink: 0, fontFamily: 'monospace'
            }}
            title={selectedProvider ? `${selectedProvider.name} (${selectedProvider.type})` : 'Sélectionnez un modèle'}
          >
            <option value="">Auto</option>
            {providers.map(p => (
              <option key={p.id} value={p.id}>{getProviderLabel(p)}</option>
            ))}
          </select>

          <button
            onClick={handleSend}
            disabled={isLoading || !inputValue.trim()}
            style={{
              padding: '9px 18px', borderRadius: '6px', border: 'none',
              backgroundColor: modeColor[chatMode], color: '#ffffff',
              fontSize: '13px', fontWeight: '600', cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
              opacity: isLoading || !inputValue.trim() ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => { if (!isLoading && inputValue.trim()) e.currentTarget.style.opacity = '0.85' }}
            onMouseLeave={(e) => { if (!isLoading && inputValue.trim()) e.currentTarget.style.opacity = '1' }}
          >
            <Send size={14} /> {isLoading ? (elapsedSecs > 0 ? `${elapsedSecs >= 60 ? `${Math.floor(elapsedSecs / 60)}m ${elapsedSecs % 60}s` : `${elapsedSecs}s`}` : '…') : 'Envoyer'}
          </button>
        </div>

        {error && (
          <div style={{ margin: '-6px 20px 10px', padding: '8px 12px', backgroundColor: '#7f1d1d', color: '#fecaca', borderRadius: '4px', fontSize: '12px' }}>
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  )
}
